import CardCommon from '@op/components/CardCommon';
import { barIcon, lineIcon, tableIcon } from '@op/constants/chart';
import useLanguage from '@op/hooks/useLanguage';
import React, { useEffect, useState } from 'react';
import { Segment, Select } from 'tea-component';
import MobileStoreTable from './MobileStoreTable';
import {
  gameStoreOneOptions,
  GameStoreOneOptionsType,
  gameStoreTwoOptions,
  GameStoreTwoOptionsType,
  mobileGameStoreLabel,
} from '../../constants';
import MobileStoreCharts from './MobileStoreCharts';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import useCompareKeyMetricsColumns from '@op/components/TableCommonHook/hooks/useCompareKeyMetricsColumns';

const subTypeMaps: {
  [key in GameStoreTwoOptionsType]: {
    [key: string]: string;
  };
} = {
  lifetime: {
    positive: 'lifetime_positive',
    negative: 'lifetime_negative',
  },
  selected: {
    positive: 'select_positive_mentions',
    negative: 'select_negative_mentions',
  },
};

export default function MobileStore({
  filter,
  gameData,
  wholeGameInfo,
}: {
  filter: any;
  gameData: any;
  wholeGameInfo: any;
}) {
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [store, setStore] = useState<GameStoreOneOptionsType>('google play');
  const [type, setType] = useState<GameStoreTwoOptionsType>('lifetime');
  const [chartType, setChartType] = useState('line');
  const [selectValue, setSelectValue] = useState({ value: '' });
  const [options, setOptions] = useState<
    Array<{ text: string; value: string }>
  >([]);

  const { downloadLegacy, downloading } = useDownloadParams();

  const { columns: gameStoreThreeOption } = useCompareKeyMetricsColumns({
    oneKey: 'GameStore',
    twoKey: store,
    threeKey: type,
    gameData,
    filter,
  });

  useEffect(() => {
    const list =
      gameStoreThreeOption
        .filter((val) => val.key !== 'game')
        .map((val) => ({
          text: val.text,
          value: val.select || subTypeMaps[type][val.key],
          ...val,
        })) || [];
    setOptions(list);
    setSelectValue({ value: list?.[0]?.value || '' });
  }, [gameStoreThreeOption, type]);

  return (
    <CardCommon
      className="scroll-margin-top-target"
      title={formatMessage({ id: 'Game Store' })}
      id="Game Store"
    >
      <CardCommon
        title={
          <>
            <Segment
              value={store}
              onChange={(value: GameStoreOneOptionsType) => {
                setStore(value);
                buriedBtnPoint('CKGS_StoreMetricsTAB', value);
              }}
              options={gameStoreOneOptions(store)}
            />
            <Segment
              value={type}
              onChange={(value: GameStoreTwoOptionsType) => {
                setType(value);
                buriedBtnPoint('CKGS_RatingMetricsTAB', value);
              }}
              options={formatOptionsMessage(gameStoreTwoOptions)}
            />
            <Select
              appearance="button"
              options={formatOptionsMessage(options)}
              value={selectValue.value}
              style={{ width: 192, fontWeight: 400 }}
              listWidth={192}
              onChange={(value: string) => {
                setSelectValue({ value });
                buriedBtnPoint('CKGS_SELECT', value);
              }}
            ></Select>
          </>
        }
        hasDownload
        contentCard
        downloadLoading={downloading}
        operations={[tableIcon, lineIcon, barIcon]}
        defaultOperation={chartType}
        handleOperationsOnClick={(item) => {
          setChartType(item?.symbol || 'line');
        }}
        handleDownloadOnClick={() => {
          const downloadLimit = 10000;

          const downLoadParams = {
            ...filter,
            stats_types: ['game_store'],
            tab_type: store,
            by_type: type,
            ...(type !== selectValue.value
              ? { sub_type: selectValue.value }
              : {}),
            gameData,
          };
          const gameStore = mobileGameStoreLabel[store];
          const gameName = wholeGameInfo?.entity_name || '';
          const fileName = `${gameName} Compare Key Metrics - DataBrain Opinions`;
          downloadLegacy({
            limit: downloadLimit,
            fileName,
            params: downLoadParams,
            downloadFunc: 'getCompareKeyMetrics',
            sheetName: `Game Store - ${gameStore}`,
          });
        }}
        logBtnId={'Mobile Game Store'}
      >
        <MobileStoreCharts
          filter={filter}
          store={store}
          type={type}
          gameData={gameData}
          chartType={chartType}
          subType={selectValue.value}
          options={options}
        />
        <MobileStoreTable
          filter={filter}
          store={store}
          type={type}
          gameData={gameData}
        ></MobileStoreTable>
      </CardCommon>
    </CardCommon>
  );
}
